import { useCallback, useMemo } from 'react';

import { useActiveProfile } from '@/store/localStorage/useTokenState';
import { debounce } from '@/utils/helpers/general';

import type { DraftBlog, EntryDraft, EntryDraftFields } from './types';
import { useEntryDraftById } from './useEntryDraftById';
import { useSetEntryDraft } from './useSetEntryDraft';

const SAVE_DELAY_MS = 500;

interface Params {
  entryId: string;
  blog: DraftBlog;
}

/** Everything the draft of one entry needs, see EntryDraftProvider. */
export function useEntryDraftModel({ entryId, blog }: Params): EntryDraft {
  const { activeProfileId } = useActiveProfile();
  const value = useEntryDraftById({ activeProfileId, entryId, blog });
  const saveEntryDraft = useSetEntryDraft();

  // a new debouncer per call: every save is written after the delay, as the
  // entry form did before the draft code was moved here
  const save = useCallback(
    (fields: EntryDraftFields) =>
      debounce(SAVE_DELAY_MS, saveEntryDraft)({ id: entryId, ...fields }, blog),
    [saveEntryDraft, entryId, blog]
  );

  const clear = useCallback(
    () => saveEntryDraft({ id: entryId, content: '' }, blog),
    [saveEntryDraft, entryId, blog]
  );

  return useMemo(() => ({ value, save, clear }), [value, save, clear]);
}
